import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ago, dateTime } from '../lib/format'
import type { ApiKey, ListResponse } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, type Column } from '../components/DataTable'
import { Button, Chip, EmptyState, Field, Modal, Panel, toast } from '../components/ui'

/**
 * Gateway integrations.
 *
 * Another telematics server (the MDVR video gateway, nlink) keeps talking to
 * its own hardware and forwards a copy of the positions for the units that
 * have been assigned here. Each gateway gets a key; the key's source name is
 * stamped on every unit the gateway registers, so one gateway can never write
 * into another's devices.
 */
export function Integrations() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [issued, setIssued] = useState<{ key: string; apiKey: ApiKey } | null>(null)

  // Gateways are wired once, by the platform. The rail hides this page from
  // everyone else; a bookmarked URL gets a plain explanation, not a 403.
  const allowed = can('apikey.manage')

  const list = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api<ListResponse<ApiKey>>('/api-keys'),
    enabled: allowed,
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Key revoked. The gateway is refused within a minute.', 'ok')
      void qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const columns: Column<ApiKey>[] = [
    {
      key: 'name',
      header: 'Gateway',
      render: (k) => (
        <div className="min-w-0">
          <div className="truncate text-[var(--color-ink)]">{k.name}</div>
          <div className="tnum text-[10px] text-[var(--color-ink-4)]">{k.prefix}…</div>
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      width: '110px',
      render: (k) => (k.source ? <Chip tone="accent">{k.source}</Chip> : '—'),
    },
    { key: 'tenant', header: 'Registers stock into', render: (k) => k.tenantName },
    {
      key: 'devices',
      header: 'Units',
      numeric: true,
      width: '80px',
      render: (k) => k.devices.toLocaleString(),
    },
    {
      key: 'used',
      header: 'Last call',
      numeric: true,
      width: '110px',
      render: (k) =>
        k.lastUsedAt ? ago(k.lastUsedAt) : <span className="text-[var(--color-ink-4)]">never</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      render: (k) =>
        k.revokedAt ? (
          <span title={`Revoked ${dateTime(k.revokedAt)}`}>
            <Chip tone="critical">revoked</Chip>
          </span>
        ) : (
          <Chip tone="ok">active</Chip>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (k) =>
        !k.revokedAt && (
          <Button
            size="sm"
            variant="ghost"
            disabled={revoke.isPending}
            onClick={() => {
              if (window.confirm(`Revoke "${k.name}"? The gateway stops forwarding until it gets a new key.`)) {
                revoke.mutate(k.id)
              }
            }}
          >
            Revoke
          </Button>
        ),
    },
  ]

  if (!allowed) {
    return (
      <Page title="Integrations" subtitle="Gateways that forward their units' positions to this platform">
        <EmptyState
          title="Not available for this account"
          body="Gateway integrations (nlink, MDVR) are connected once by the platform; units arrive in your stock pool automatically."
        />
      </Page>
    )
  }

  return (
    <Page
      title="Integrations"
      subtitle="Gateways that forward their units' positions to this platform"
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Icon name="plus" size={13} />
          New gateway key
        </Button>
      }
    >
      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(k) => k.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          empty={{
            title: 'No gateways connected',
            body: 'Create a key for each gateway, such as the MDVR server or nlink, and configure the gateway with it.',
          }}
        />
      </Panel>

      <div className="mt-4">
        <Panel title="How a gateway connects">
          <div className="text-[13px] text-[var(--color-ink-2)] leading-relaxed space-y-2">
            <p>
              Every call sends <code className="tnum">Authorization: Bearer &lt;key&gt;</code> to{' '}
              <code className="tnum">{window.location.origin}/ingest/v1</code>.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <code>PUT /inventory</code>: the gateway lists its units. New ones appear under Devices as
                stock, ready to transfer to a client.
              </li>
              <li>
                <code>GET /devices</code>: the units assigned to a client. The gateway polls this every
                minute and forwards only those.
              </li>
              <li>
                <code>POST /positions</code>: batches of fixes, gzip welcome. Anything for an unassigned unit
                is skipped.
              </li>
            </ul>
            <p>The gateway keeps all its own data; this platform only receives a copy for assigned units.</p>
          </div>
        </Panel>
      </div>

      {creating && (
        <CreateKeyModal
          onClose={() => setCreating(false)}
          onDone={(r) => {
            setCreating(false)
            setIssued(r)
            void qc.invalidateQueries({ queryKey: ['api-keys'] })
          }}
        />
      )}

      {issued && <IssuedKeyModal issued={issued} onClose={() => setIssued(null)} />}
    </Page>
  )
}

function CreateKeyModal({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: (r: { key: string; apiKey: ApiKey }) => void
}) {
  const [name, setName] = useState('')
  const [source, setSource] = useState('')
  const validSource = /^[a-z][a-z0-9_-]{1,31}$/.test(source)

  const create = useMutation({
    mutationFn: () =>
      api<{ key: string; apiKey: ApiKey }>('/api-keys', {
        body: { name: name.trim(), source },
      }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="New gateway key"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || !validSource || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <Field label="Name" required hint="For people: which server this is">
          <input
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="MDVR gateway (mdvr.ketchup.cloud)"
            autoFocus
          />
        </Field>
        <Field
          label="Source"
          required
          error={source && !validSource ? 'Lowercase letters, digits, - or _, starting with a letter' : undefined}
          hint="Stamped on every unit this gateway registers, e.g. mdvr or nlink. Keep the same source when you replace a key."
        >
          <input
            className="field tnum"
            value={source}
            onChange={(e) => setSource(e.target.value.toLowerCase())}
            placeholder="mdvr"
            maxLength={32}
          />
        </Field>
      </div>
    </Modal>
  )
}

function IssuedKeyModal({
  issued,
  onClose,
}: {
  issued: { key: string; apiKey: ApiKey }
  onClose: () => void
}) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.key)
      toast('Key copied', 'ok')
    } catch {
      toast('Copy failed. Select the key and copy it manually.', 'error')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Copy the key now"
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3.5">
        <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
          This is the only time the key for <strong>{issued.apiKey.name}</strong> is shown. Only its hash is
          stored, so a lost key has to be replaced.
        </p>
        <div className="flex gap-2">
          <input className="field tnum grow" readOnly value={issued.key} onFocus={(e) => e.target.select()} />
          <Button onClick={() => void copy()}>Copy</Button>
        </div>
      </div>
    </Modal>
  )
}
